import { MultilegTooltip } from '@src/components/Multileg/MultilegTooltip';
import {
  type AgGridMeterProps,
  Button,
  ButtonGroup,
  Card,
  Flex,
  FormControlSizes,
  getSymbolsAcrossMarkets,
  IconName,
  isMultileg,
  isOrderRejected,
  Meter,
  type MeterWithValueProps,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Order,
  OrderCardHeader,
  Popover,
  PopperHeader,
  ToggleButton,
  UnifiedLiquidityEnum,
  useMixpanel,
  usePopoverState,
  useSecurity,
  useStrategiesContext,
} from '@talos/kyoko';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTradingOrderDetailsRoute } from '../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../hooks/useAppLayoutId';
import { LAYOUT_HEADER_HEIGHT } from '../../styles/dimensions';
import { FilledMarketDistributionChart } from './FilledMarketDistributionChart';

function MarketDistributionTooltip({
  order,
  meterProps,
  close,
  onHeightChange,
}: {
  order: Order;
  meterProps: MeterWithValueProps;
  close: () => void;
  onHeightChange: () => void;
}) {
  const mixpanel = useMixpanel();
  const { strategiesByName } = useStrategiesContext();
  const selectedStrategy = order.Strategy ? strategiesByName?.get(order.Strategy) : undefined;
  const underlyingSymbols = getSymbolsAcrossMarkets(order);
  const navigate = useNavigate();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number>(0);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutId = useCurrentAppLayoutId();

  // ResizeObserver to monitor height changes
  useEffect(() => {
    if (!tooltipRef.current || !onHeightChange) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const currentHeight = entry.contentRect.height;
      const previousHeight = previousHeightRef.current;

      // Only trigger update if height actually changed
      if (Math.abs(currentHeight - previousHeight) > 1) {
        previousHeightRef.current = currentHeight;

        // Debounce the update to prevent rapid successive calls
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          onHeightChange();
        }, 16); // ~60fps debounce
      }
    });

    resizeObserver.observe(tooltipRef.current);

    return () => {
      resizeObserver.disconnect();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [onHeightChange]);

  const handleOpenAnalytics = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      close();
      mixpanel.track(MixpanelEvent.ViewFullAnalytics, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.FilledPercentModal,
      });
      navigate(
        getTradingOrderDetailsRoute({
          orderID: order.OrderID,
          type: 'order',
          tab: 'analytics',
          layoutId,
        })
      );
    },
    [navigate, order.OrderID, close, mixpanel, layoutId]
  );

  return (
    <Flex
      ref={tooltipRef}
      flexDirection="column"
      gap="spacing8"
      w="520px"
      minHeight="300px"
      maxHeight={`calc(100vh - ${LAYOUT_HEADER_HEIGHT * 2}px)`}
      overflowY="auto"
      data-testid="market-distribution"
    >
      <PopperHeader
        icon={IconName.ChartDonut}
        title="Market Distribution"
        suffix={
          <Button startIcon={IconName.Exit} size={FormControlSizes.Small} onClick={handleOpenAnalytics}>
            View Full Analytics
          </Button>
        }
        onCloseClicked={close}
      />
      <Flex flexDirection="column" p="spacing8" gap="spacing4">
        <OrderCardHeader order={order} selectedStrategy={selectedStrategy} underlyingSymbols={underlyingSymbols} />
        <MultilegTooltip meterProps={meterProps} data={order} />
        <FilledMarketDistribution order={order} />
      </Flex>
    </Flex>
  );
}

export function MarketDistributionTooltipRenderer(props: { meterProps: MeterWithValueProps } & AgGridMeterProps) {
  const mixpanel = useMixpanel();
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'right',
    delay: 500,
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'maxHeight',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
    onOpen: () => {
      mixpanel.track(MixpanelEvent.ViewMarketDistributionTooltip, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
      });
    },
  });

  const handleHeightChange = useCallback(() => {
    if (popover.update) {
      popover.update();
    }
  }, [popover]);

  const { data, meterProps } = props;

  if (!data || !(data instanceof Order) || !data.OrderID || isOrderRejected(data.OrdStatus)) {
    return null;
  }

  return (
    <Popover {...popover} targetStyle={{ width: '100%' }}>
      <Meter {...props.meterProps} />
      <MarketDistributionTooltip
        order={data}
        meterProps={meterProps}
        close={popover.close}
        onHeightChange={handleHeightChange}
      />
    </Popover>
  );
}

interface FilledMarketDistributionProps {
  order: Order;
}

function FilledMarketDistribution({ order }: FilledMarketDistributionProps) {
  const security = useSecurity(order.Symbol);
  const isOrderMultiLeg = isMultileg(security);
  const isUnifiedLiquidity = order.unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
  const [activeLeg, setActiveLeg] = useState<0 | 1 | undefined>(isOrderMultiLeg && !isUnifiedLiquidity ? 0 : undefined);

  const actions = useMemo(() => {
    return (
      isOrderMultiLeg && (
        <ButtonGroup size={FormControlSizes.Small}>
          {isUnifiedLiquidity && (
            <ToggleButton onClick={() => setActiveLeg(undefined)} selected={activeLeg === undefined}>
              Order
            </ToggleButton>
          )}
          <ToggleButton onClick={() => setActiveLeg(0)} selected={activeLeg === 0}>
            Leg 1
          </ToggleButton>
          <ToggleButton onClick={() => setActiveLeg(1)} selected={activeLeg === 1}>
            Leg 2
          </ToggleButton>
        </ButtonGroup>
      )
    );
  }, [activeLeg, isOrderMultiLeg, isUnifiedLiquidity]);

  return (
    <Card actions={actions}>
      <FilledMarketDistributionChart order={order} legIndex={activeLeg} />
    </Card>
  );
}
